import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import Box from 'components/UI/Box/Box';
import Button from 'components/UI/Button';
import InlineError from 'components/UI/InlineError';
import {
  fetchOwnReferrerCode,
  fetchReferrals,
  NO_REFERRAL_INFO_CODE,
} from 'modules/referrals/actions';
import { copyToClipboard } from 'utils/events';
import useLiterals from 'utils/hooks/useLiterals';
import ReferralListItem from './components/ReferralListItem';
import './styles.scss';

const COMPLETED_STATUS = '04';

/** Suma los puntos del referidor solo en referidos completados. */
export const sumCompletedReferrerPoints = (referrals) => (
  (referrals || []).reduce((total, item) => {
    if (String(item?.Estatus) !== COMPLETED_STATUS) return total;
    if (typeof item?.PuntosReferidor !== 'number') return total;
    return total + item.PuntosReferidor;
  }, 0)
);

const Referrals = () => {
  const literals = useLiterals('referrals');
  const [codeView, setCodeView] = useState({ status: 'loading', code: null });
  const [listView, setListView] = useState({ status: 'loading', referrals: [] });

  const loadCode = useCallback(() => {
    setCodeView({ status: 'loading', code: null });
    return fetchOwnReferrerCode()
      .then((data) => {
        setCodeView({ status: 'success', code: data.ownReferralCode });
      })
      .catch(() => {
        setCodeView({ status: 'error', code: null });
      });
  }, []);

  const loadList = useCallback(() => {
    setListView({ status: 'loading', referrals: [] });
    return fetchReferrals()
      .then((data) => {
        setListView({ status: 'success', referrals: data.referrals || [] });
      })
      .catch((error) => {
        if (error?.code === NO_REFERRAL_INFO_CODE) {
          setListView({ status: 'success', referrals: [] });
          return;
        }
        setListView({ status: 'error', referrals: [] });
      });
  }, []);

  useEffect(() => {
    let active = true;

    fetchOwnReferrerCode()
      .then((data) => {
        if (active) {
          setCodeView({ status: 'success', code: data.ownReferralCode });
        }
      })
      .catch(() => {
        if (active) setCodeView({ status: 'error', code: null });
      });

    fetchReferrals()
      .then((data) => {
        if (active) {
          setListView({ status: 'success', referrals: data.referrals || [] });
        }
      })
      .catch((error) => {
        if (!active) return;
        if (error?.code === NO_REFERRAL_INFO_CODE) {
          setListView({ status: 'success', referrals: [] });
          return;
        }
        setListView({ status: 'error', referrals: [] });
      });

    return () => {
      active = false;
    };
  }, []);

  const code = codeView.code;
  const hasCode = typeof code === 'string' && code.length > 0;
  const referrals = listView.referrals || [];
  // Intro solo cuando la lista ya resolvió vacío (éxito o 400 conocido).
  // No asumir vacío mientras carga ni ante error genérico de lista.
  const listResolvedEmpty = listView.status === 'success' && referrals.length === 0;
  const hasReferrals = listView.status === 'success' && referrals.length > 0;
  const showIntroCards = listResolvedEmpty;
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const showPointsTotal = listView.status === 'success';
  const accumulatedPoints = sumCompletedReferrerPoints(referrals);

  const handleCopy = () => {
    if (!hasCode) return;
    copyToClipboard(code).then((success) => {
      if (success) {
        toast.success(literals.copySuccess, { autoClose: 2000 });
      } else {
        toast.error(literals.copyError, { autoClose: 2000 });
      }
    });
  };

  const handleShare = () => {
    if (!hasCode || typeof navigator.share !== 'function') return;
    navigator.share({ text: code }).catch((error) => {
      if (error?.name === 'AbortError') return;
      toast.error(literals.shareError, { autoClose: 2000 });
    });
  };

  return (
    <Layout className='referrals-page' title={literals.title} bottomMenu onBack>
      <AppShell variant='wide'>
        <h1 className='referrals-page__title mb-2'>{literals.title}</h1>
        <p className='referrals-page__intro mb-6'>{literals.intro}</p>

        {showIntroCards && (
          <>
            <Box className='referrals-section mb-4' padding='sm'>
              <h2 className='referrals-section__heading mb-3'>{literals.howItWorksTitle}</h2>
              <ol className='referrals-steps mb-0'>
                <li>{literals.howItWorksStep1}</li>
                <li>{literals.howItWorksStep2}</li>
                <li>{literals.howItWorksStep3}</li>
              </ol>
            </Box>

            <Box className='referrals-section mb-4' padding='sm'>
              <h2 className='referrals-section__heading mb-3'>{literals.considerationsTitle}</h2>
              <ul className='referrals-considerations mb-0'>
                <li>{literals.considerationsItem1}</li>
                <li>{literals.considerationsItem2}</li>
              </ul>
            </Box>
          </>
        )}

        <Box className='referral-code-card mb-4' padding='sm'>
          <h2 className='referrals-section__heading mb-3'>{literals.myCode}</h2>
          {codeView.status === 'loading' && (
            <p role='status' className='mb-0'>{literals.codeLoading}</p>
          )}
          {codeView.status === 'error' && (
            <>
              <InlineError error={literals.codeLoadError} className='mb-4' />
              <Button text={literals.retry} onClick={loadCode} />
            </>
          )}
          {codeView.status === 'success' && (
            hasCode ? (
              <>
                <div className='referral-code-card__value mb-3'>
                  <p className='referral-code mb-0'>{code}</p>
                </div>
                <div className='referral-actions'>
                  <Button
                    text={literals.copy}
                    size='sm'
                    onClick={handleCopy}
                  />
                  {canShare && (
                    <Button
                      text={literals.share}
                      type='secondary'
                      size='sm'
                      onClick={handleShare}
                    />
                  )}
                  {showPointsTotal && (
                    <p className='referral-code-card__points mb-0'>
                      {`+ ${accumulatedPoints} ${literals.pts}`}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className='mb-1'>{literals.noCodeTitle}</p>
                <p className='mb-0'>{literals.noCodeBody}</p>
              </>
            )
          )}
        </Box>

        {hasReferrals && (
          <h2 className='referrals-section__heading mb-3'>{literals.myReferrals}</h2>
        )}

        {listView.status === 'loading' && (
          <p role='status'>{literals.listLoading}</p>
        )}

        {listView.status === 'error' && (
          <Box padding='sm'>
            <InlineError error={literals.listLoadError} className='mb-4' />
            <Button text={literals.retry} onClick={loadList} />
          </Box>
        )}

        {hasReferrals && referrals.map((item, index) => (
          <ReferralListItem
            key={`referral-${index}`}
            item={item}
            literals={literals}
          />
        ))}
      </AppShell>
    </Layout>
  );
};

export default Referrals;
